// Landing screen for a signed-in user who belongs to no household yet -- an
// invited person who signed up without getting a household of their own
// (handle_new_user skips creating one when an invite is pending, 0041), or
// someone who declined/let expire their only invite. They either accept an
// invite or start their own household. Shown by App.jsx instead of the app.
//
// Accepting reloads the page onto Plan Defaults: the "People" section there
// is where a new member claims (or adds) their own row, so their nutrition
// targets are private to them from the start.
import { useState, useEffect } from "react";
import { COLORS, FONT_SIZE, RADIUS, SPACE } from "../lib/constants";
import { loadMyPendingHouseholdInvites, acceptHouseholdInvite, declineHouseholdInvite, createOwnHousehold } from "../lib/db";

export function NoHouseholdScreen({ userEmail, onSignOut, onReload }) {
  const { paper, card, ink, soft, accent, danger, line } = COLORS;
  const [invites, setInvites] = useState(null); // null = still loading
  const [shareRecipes, setShareRecipes] = useState({}); // inviteId -> boolean
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    loadMyPendingHouseholdInvites()
      .then((rows) => {
        if (!cancelled) setInvites(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setInvites([]);
          setError(e.message || "Couldn't check for invitations.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(e.message || "Something went wrong — try again.");
      setBusy(false);
    }
  }

  const accept = (invite) =>
    run(async () => {
      await acceptHouseholdInvite(invite.id, !!shareRecipes[invite.id]);
      window.location.assign("/defaults");
    });

  const decline = (invite) =>
    run(async () => {
      await declineHouseholdInvite(invite.id);
      setInvites((list) => (list || []).filter((i) => i.id !== invite.id));
      setBusy(false);
    });

  const startOwn = () =>
    run(async () => {
      await createOwnHousehold();
      onReload();
    });

  const buttonBase = { padding: "var(--space-lg) var(--space-xl2)", fontSize: FONT_SIZE.md, fontWeight: 600, borderRadius: RADIUS.lg, border: "none", cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 };

  return (
    <div style={{ background: paper, color: ink, fontFamily: "var(--font-serif)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: SPACE.x4, boxSizing: "border-box" }}>
      <div style={{ background: card, border: `2px solid ${line}`, borderRadius: RADIUS.xxl, padding: SPACE.x5, maxWidth: 460, width: "100%" }}>
        <h1 style={{ fontSize: FONT_SIZE.h2, fontWeight: 700, margin: "0 0 var(--space-xs)" }}>The weekly kitchen</h1>
        <p className="mp-sans" style={{ color: soft, fontSize: FONT_SIZE.base, margin: "0 0 var(--space-xxl)" }}>
          Signed in as {userEmail}
        </p>

        {invites === null && <p className="mp-sans" style={{ color: soft, fontSize: FONT_SIZE.md }}>Checking for invitations…</p>}

        {(invites || []).map((invite) => (
          <div key={invite.id} className="mp-sans" style={{ background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)", marginBottom: SPACE.xl2 }}>
            <p style={{ fontSize: FONT_SIZE.md, margin: "0 0 var(--space-lg)", color: ink }}>
              <strong>{invite.invitedByEmail}</strong> invited you to join their household. You'll share their meal plans, grocery lists and settings.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: SPACE.md, fontSize: FONT_SIZE.base, color: soft, marginBottom: SPACE.xl }}>
              <input
                type="checkbox"
                className="mp-check"
                checked={!!shareRecipes[invite.id]}
                onChange={(e) => setShareRecipes((s) => ({ ...s, [invite.id]: e.target.checked }))}
                style={{ marginTop: SPACE.hair }}
              />
              <span>
                Let people in this household see my recipes (view only). You can change this any time. Any recipe you plan with here is visible to the household while it's in a plan, whatever you choose.
              </span>
            </label>
            <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
              <button type="button" disabled={busy} onClick={() => accept(invite)} style={{ ...buttonBase, background: accent, color: COLORS.onAccent }}>
                Join this household
              </button>
              <button type="button" disabled={busy} onClick={() => decline(invite)} style={{ ...buttonBase, background: "transparent", border: `1px solid ${line}`, color: ink }}>
                Decline
              </button>
            </div>
          </div>
        ))}

        {invites !== null && (
          <div className="mp-sans" style={{ borderTop: invites.length > 0 ? `1px dashed ${line}` : "none", paddingTop: invites.length > 0 ? SPACE.xl2 : 0 }}>
            <p style={{ fontSize: FONT_SIZE.md, color: soft, margin: "0 0 var(--space-xl)" }}>
              {invites.length > 0 ? "Or keep things separate and set up a household of your own." : "You aren't part of a household yet. Set one up to start planning."}
            </p>
            <button type="button" disabled={busy} onClick={startOwn} style={{ ...buttonBase, background: accent, color: COLORS.onAccent }}>
              Start my own household
            </button>
          </div>
        )}

        {error && (
          <p className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.base, margin: "var(--space-xl2) 0 0" }}>
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onSignOut}
          className="mp-sans"
          style={{ display: "block", marginTop: SPACE.xxl, fontSize: FONT_SIZE.sm, background: "none", border: "none", color: soft, textDecoration: "underline", cursor: "pointer", padding: 0 }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
